'use client'

import { motion, type Variants } from 'framer-motion'
import Image from 'next/image'
import Link from 'next/link'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/blocks/components/ui/button'
import { cn } from '@/lib/utils'
import { PublicCourseCardDto } from '@/server/public-api/types'
import { buildLocalePath } from '@/utils'

type CoursesListProps = {
  courses: PublicCourseCardDto[]
  locale: string
  /** Grid override for narrower contexts (the related strip fits 3 cards on one row). */
  gridClassName?: string
}

const CoursesList = ({ courses, locale, gridClassName }: CoursesListProps) => {
  const scrollRef = useRef(null)
  const { t } = useTranslation()
  const cardVariants: Variants = {
    hidden: { opacity: 0, y: 24 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        // spring = smooth & snappy
        type: 'spring',
        stiffness: 120,
        damping: 18,
        mass: 0.7,
        // delay children slightly for a nice cascade
        staggerChildren: 0.08,
        // when should children start animating
        when: 'beforeChildren',
      },
    },
  }

  return (
    <ul
      ref={scrollRef}
      className={cn(
        'mx-auto grid place-content-center gap-x-14 gap-y-16  sm:grid-cols-[repeat(auto-fit,24rem)]',
        gridClassName
      )}
    >
      {courses.map(course => (
        <motion.li
          key={course.id}
          className='h-full'
          variants={cardVariants}
          initial='hidden'
          whileInView='show'
          viewport={{ root: scrollRef, once: true, amount: 0.2, margin: '0px 0px -10% 0px' }}
        >
          <section className='flex h-full flex-col overflow-hidden rounded-[25px] bg-primary-light text-center shadow-md max-sm:min-h-[90vh] sm:rounded-[50px]'>
            <Image
              src={course.coverImageUrl || '/hafizalquran-og.jpeg'}
              width={384}
              height={443}
              alt={course.imageAlt || course.title}
              loading='lazy'
              sizes='(min-width: 768px) 384px, 100vw'
              className='h-auto w-full object-cover object-top md:h-[443px] md:w-[384px] '
            />
            <div className='flex grow flex-col items-center space-y-8 bg-white px-4 py-10'>
              <p className='text-[24px] font-semibold text-black sm:text-2xl'>{course.title}</p>
              <p className='grow text-start text-[18px]  text-black  sm:text-[18px]'>{course.shortDescription}</p>
              <Button asChild className='size-auto bg-primary py-3 text-[24px] font-medium text-black sm:text-lg'>
                <Link
                  href={buildLocalePath(locale, course.slug ? `/courses/${course.slug}` : '/courses')}
                  className='block'
                >
                  {t('courses.button.details')}
                </Link>
              </Button>
            </div>
          </section>
        </motion.li>
      ))}
    </ul>
  )
}

export default CoursesList
